import { Button, Flex, Menu, MenuProps, Space } from 'antd';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import AppDivider from 'src/components/appDivider';
import AppPrimaryButton from 'src/components/appPrimaryButton/AppPrimaryButton';
import LangugeSelect from 'src/components/LangugeSelect';
import { APP_ROUTES, USER_ACTIVITY_EVENT, USER_ACTIVITY_TYPE } from 'src/enums';
import { userSelectors, useSelector } from 'src/store';
import { useTheme } from 'src/styles/theme';
import { createLog, logOut } from 'src/utils';
import styled from 'styled-components';

import classes from './style/SideMenu.module.css';

interface ISideMenuProps {
  activeKey: string;
}

const StyledMenuWrapper = styled.div`
  .ant-menu-item {
    height: 45px;
    line-height: 45px;
    margin: 5px;
    font-family: 'Noto Sans Hebrew';
    font-style: normal;
    font-weight: 500;
    font-size: ${({ theme }) => theme.fonts.fontSizes.fontSize16}px;
    line-height: 20px;
  }
  .ant-menu-item .ant-menu-title-content {
    color: ${({ theme }) => theme.colors.commentGrey};
  }
  .ant-menu-item:hover {
    background-color: ${({ theme }) => theme.colors.iceBlue};
    height: 45px;
  }
`;

const SideMenu: React.FunctionComponent<ISideMenuProps> = (props) => {
  const { t } = useTranslation();
  const { activeKey } = props;
  const navigate = useNavigate();
  const theme = useTheme();
  //SELECTORS
  const hospital = useSelector(userSelectors.selectUserHospital);
  const user = useSelector(userSelectors.selectUser);

  interface MenuItem {
    key: string;
    label: string;
    icon: React.ReactNode;
    itemBlue: React.ReactNode;
    route?: string;
  }

  const items: MenuItem[] = [
    {
      key: APP_ROUTES.HOME,
      label: t('medicalHistory.homePage'),
      icon: <img src="/assets/icons/homePageIcon.png" />,
      itemBlue: (
        <Flex>
          <div className={classes.gradient}></div>
          <img src="/assets/icons/homePageIcon_blue.png" />
        </Flex>
      ),
      route: APP_ROUTES.HOME
    },
    {
      key: APP_ROUTES.RADIOLOGY,
      label: t('medicalHistory.radiology'),
      icon: <img src="/assets/icons/radiologyIcon.png" />,
      itemBlue: (
        <Flex>
          <div className={classes.gradient}></div>
          <img src="/assets/icons/radiologyIcon_Blue.png" />
        </Flex>
      ),
      route: APP_ROUTES.RADIOLOGY
    },
    {
      key: APP_ROUTES.LAB,
      label: t('medicalHistory.lab'),
      icon: <img src="/assets/icons/labPageIcon.png" />,
      itemBlue: (
        <Flex>
          <div className={classes.gradient}></div>
          <img src="/assets/icons/labPageIcon_Blue.png" />
        </Flex>
      ),
      route: APP_ROUTES.LAB
    },
    {
      key: APP_ROUTES.VISIT_SUMMARY,
      label: t('medicalHistory.visitSummary'),
      icon: <img src="/assets/icons/visitSummaryIcon.png" />,
      itemBlue: (
        <Flex>
          <div className={classes.gradient}></div>
          <img src="/assets/icons/visitSummaryIcon_Blue.png" />
        </Flex>
      ),
      route: APP_ROUTES.VISIT_SUMMARY
    },
    {
      key: APP_ROUTES.ER_SUMMARY,
      label: t('medicalHistory.erSummary'),
      icon: <img src="/assets/icons/visitSummaryIcon.png" />,
      itemBlue: (
        <Flex>
          <div className={classes.gradient}></div>
          <img src="/assets/icons/visitSummaryIcon_Blue.png" />
        </Flex>
      ),
      route: APP_ROUTES.ER_SUMMARY
    },
    {
      key: APP_ROUTES.RELEASE_DOCS,
      label: t('medicalHistory.releaseDoc'),
      icon: <img src="/assets/icons/hospitalityIcon.png" />,
      itemBlue: (
        <Flex>
          <div className={classes.gradient}></div>
          <img src="/assets/icons/hospitalityIcon_Blue.png" />
        </Flex>
      ),
      route: APP_ROUTES.RELEASE_DOCS
    }
  ];

  const onLogout = async () => {
    await logOut();
  };

  const onClickZimunTorim = () => {
    if (!hospital?.zimunTorimLink) {
      console.log('There is no zimun torim link');
      return;
    }

    let zimunTorimLink = hospital.zimunTorimLink;
    if (zimunTorimLink.endsWith('&tkn=')) {
      if (!user) {
        console.log('No user!!!'); //TODO:error modal and error log
        return;
      }
      if (!user.ZimunTorimToken) {
        console.log('No wl token!!!'); //TODO:error modal and error log
        return;
      }

      zimunTorimLink += user.ZimunTorimToken;
    }

    createLog(
      user,
      USER_ACTIVITY_EVENT.VIEW,
      USER_ACTIVITY_TYPE.ZIMUN_TORIM,
      USER_ACTIVITY_TYPE.ZIMUN_TORIM
    );
    window.open(zimunTorimLink, '_blank');
  };

  const onClick: MenuProps['onClick'] = async (e) => {
    navigate(`${e.key}`);
  };

  const getMenuItems = items.map((item) => ({
    ...item,
    icon: item.key === activeKey ? item.itemBlue : item.icon
  }));

  return (
    <Flex vertical className={classes.sideMenuContainer}>
      <StyledMenuWrapper>
        <Flex
          align="center"
          justify="start"
          className={classes.hospitalContainer}
        >
          <Space>
            <img
              src={hospital?.logo || '/assets/logo_hativa.png'}
              className={classes.logo}
            />
            <p className={`${theme.fonts.classNames.cardSubtitle} `}>
              {`${t('login.medicalCenter')} ${hospital?.name}`}
            </p>
          </Space>
        </Flex>
        <AppDivider mt={15} mb={15} />
        <Flex
          justify="space-between"
          align="center"
          className={classes.userContainer}
        >
          <Flex>
            <Space>
              <img src="/assets/icons/avatar.png" />
              <p className={`${theme.fonts.classNames.cardSubtitle} `}>
                {user?.FullName}
              </p>
            </Space>
          </Flex>
        </Flex>
        <AppDivider mt={15} mb={15} />

        <Menu
          mode="inline"
          selectedKeys={[activeKey]}
          onClick={onClick}
          items={getMenuItems}
        />
      </StyledMenuWrapper>
      <Flex vertical justify="end" className={classes.sideMenuEnd}>
        <Flex justify="center">
          <AppPrimaryButton
            onClick={onClickZimunTorim}
            className={classes.zimunTorimButton}
          >
            {t('sideMenu.zimunTorim')}
          </AppPrimaryButton>
        </Flex>

        <AppDivider />
        <Flex justify="space-between">
          <Button className={classes.logoutButton} onClick={onLogout}>
            {t('sideMenu.logout')}
          </Button>
          <Flex className={classes.languageButton}>
            <LangugeSelect />
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
};

export default SideMenu;
